/**
 * core/league — pure, deterministic helpers for the Elite Four + Champion gauntlet
 * (Round 3, elite-four.md). No rendering, no IO. The League is a de-stressed capstone:
 * 5 bouts (Elite 1→4 → Champion) fought back-to-back with a full HEAL between each and
 * exit-anytime / resume-anytime. These helpers own the tiny ordering/resume math so the
 * session verb + the scene stay thin and the no-pressure invariants are unit-testable.
 *
 * The load-bearing invariants (see the no-pressure audit in the doc):
 *  - a WIN advances the bout; a LOSS changes NOTHING (you resume the same bout, healed);
 *  - boutIndex only ever moves forward within a run, capped at the count;
 *  - clearing the Champion appends a Hall of Fame page and resets the run for a rematch.
 */
import type { LeagueState, HallOfFameRecord } from '@/shared';

/** Bouts in a full run: Elite 1..4 + the Champion. boutIndex === this ⇒ run cleared. */
export const LEAGUE_BOUT_COUNT = 5;

/** The bout the player faces next (0..LEAGUE_BOUT_COUNT). Clamped defensively. */
export function nextBout(state: LeagueState): number {
  return Math.max(0, Math.min(LEAGUE_BOUT_COUNT, state.boutIndex));
}

/** Has the Champion (final bout) been beaten this run? */
export function isRunCleared(state: LeagueState): boolean {
  return state.boutIndex >= LEAGUE_BOUT_COUNT;
}

/** Begin a fresh run: underway, back at Elite 1. (The heal is a session concern.) */
export function startRun(state: LeagueState): LeagueState {
  return { ...state, inProgress: true, boutIndex: 0 };
}

/**
 * Advance after a WON bout — the ONLY thing that moves boutIndex forward. Capped at
 * LEAGUE_BOUT_COUNT so a stray extra win can never overrun the array. Pure.
 */
export function advanceOnWin(state: LeagueState): LeagueState {
  return { ...state, boutIndex: Math.min(LEAGUE_BOUT_COUNT, state.boutIndex + 1) };
}

/**
 * Record a Champion clear: append the team snapshot to the Hall of Fame, bump the
 * rematch counter, and reset the run to Elite 1 (inProgress false) so the Summit is
 * freely re-enterable. The Hall of Fame keeps EVERY clear — a warm scrapbook, never a
 * leaderboard. Pure.
 */
export function recordClear(state: LeagueState, snapshot: HallOfFameRecord): LeagueState {
  return {
    ...state,
    boutIndex: 0,
    inProgress: false,
    clears: state.clears + 1,
    hallOfFame: [...state.hallOfFame, snapshot],
  };
}
